import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { Subscription } from 'rxjs';
import { UserLiteDTO } from 'src/app/common/state/user/user.dto';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import {
  dateFromUnixTimestamp,
  ngbDateStructFromUnixTimestamp,
} from 'src/app/common/utilities/date-helpers';
import { checkIfE2L } from 'src/app/common/utilities/role-helpers';
import { FileDTO } from '../../../dtos/file.dto';
import { UploadType } from '../../../enums/upload-type.enum';
import { AlertService } from '../../../services/alert/alert.service';
import { PlanActionItemFileService } from '../../../services/file/plan-action-item.file.service';
import { PlansPusherService } from '../../../services/plans/plans-pusher.service';

import { CommentModelDTO } from '../../../dtos/comment.dto';
import { CommentsService } from '../../../services/comments/comments.service';
import { PlansService } from '../../../services/plans/plans.service';
import { PusherAssignmentChangesDTO } from '../../../services/plans/plans.service.dto';
import {
  AddAssignment,
  DeleteActionItem,
  DeleteAssignment,
  EditActionItem,
} from '../../../state/implementation-plan/implementation-plan.actions';
import { EditActionItemPayload } from '../../../types/payloads/plan.payloads';
import {
  LinkedItemSearchOption,
  LinkedItemTargetTable,
} from '../../../types/responses/linked-items.responses';
import {
  ActionItemAPIResponse,
  DeliverableAPIResponse,
  StatusType,
} from '../../../types/responses/plan.responses';
import { CkEditorComponent } from '../../ck-editor/ck-editor.component';
import {
  DatepickerComponent,
  DatepickerOutput,
} from '../../datepicker/datepicker.component';
import { FileListComponent } from '../../file-management/file-list/file-list.component';
import {
  FileUploadComponent,
  UploadStyles,
} from '../../file-management/file-upload/file-upload.component';
import { ModalComponent } from '../../modals/modal/modal.component';

@Component({
  selector: 'app-edit-action-item-modal',
  templateUrl: './edit-action-item-modal.component.html',
  styleUrl: './edit-action-item-modal.component.scss',
  standalone: false,
})
export class EditActionItemModalComponent
  implements OnInit, OnChanges, OnDestroy
{
  @ViewChild('modal')
  modal: ModalComponent;

  @ViewChild('dueDatePicker') dueDatePicker: DatepickerComponent;

  @ViewChild('wysiwyg') public editor: CkEditorComponent;

  @ViewChild('uploadComponent') uploadComponent: FileUploadComponent;

  @ViewChild('fileListComponent') fileListComponent: FileListComponent;

  @Input() displayName: string;

  @Input() actionItem: ActionItemAPIResponse;

  @Input() phaseId: number;

  @Input() parentDeliverable: DeliverableAPIResponse;

  @Input() districtId: number;

  user: User;

  isE2L = false;

  subscriptions = new Subscription();

  form: FormGroup;

  formSubmitted = false;

  selectedUserIds: number[] = [];

  editActionItemPayload: EditActionItemPayload;

  defaultDueDate: number;

  datePickerMinDate: Date;

  datePickerMaxDate: Date;

  isLoading = false;

  errorMessage = '';

  uploadStyle = UploadStyles.SIMPLE;

  uploadType = UploadType.ACTION_ITEM_ATTACHMENT;

  attachments: FileDTO[] = [];

  commentRequest: Subscription;

  commentList: CommentModelDTO[];

  updatedContent = '';

  selectedStatus: StatusType;

  assignees: UserLiteDTO[] = [];

  linkedItems: LinkedItemSearchOption[] = [];

  readonly LinkedItemTargetTable = LinkedItemTargetTable;

  constructor(
    private plansService: PlansService,
    private plansPusherService: PlansPusherService,
    private planActionItemFileService: PlanActionItemFileService,
    private alertService: AlertService,
    private commentsService: CommentsService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    private store: Store
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
    this.isE2L = checkIfE2L(this.user);
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.plansPusherService.actionItemCommentNotifier.subscribe(() => {
        this.getComments();
      })
    );
    this.subscriptions.add(
      this.plansPusherService.actionItemAttachmentNotifier.subscribe(() => {
        this.getAttachments();
      })
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.actionItem && changes['actionItem']) {
      this.updateNotes(
        this.actionItem.content === null ? '' : this.actionItem.content
      );
      this.loadActionItemData();
      if (
        changes['actionItem'].currentValue?.id !==
        changes['actionItem'].previousValue?.id
      ) {
        this.getComments();
        this.getAttachments();
      }
    }
  }

  get f() {
    return this.form.controls;
  }

  loadActionItemData() {
    if (this.actionItem) {
      this.setInitialDueDate(this.actionItem.due_date);
      this.form = this.formBuilder.group({
        title: [this.actionItem.title, Validators.required],
        description: [this.actionItem.description],
        status: [this.actionItem.status],
        dueDate: [dateFromUnixTimestamp(this.defaultDueDate * 1000)],
      });

      this.planActionItemFileService.setActionItemId(this.actionItem.id);
      this.selectedStatus = this.setLocalStatus(this.actionItem.status);
      this.assignees = [];
      this.actionItem.assignments.forEach((assignment) => {
        // Check if the user is already in the assignees array
        if (
          !this.assignees.some((assignee) => assignee.id === assignment.user.id)
        ) {
          this.assignees.push(assignment.user);
        }
      });

      this.datePickerMinDate = new Date(this.parentDeliverable.start_date);
      this.datePickerMinDate.setDate(this.datePickerMinDate.getDate() + 1);
      this.datePickerMaxDate = new Date(this.parentDeliverable.due_date);
      this.datePickerMaxDate.setDate(this.datePickerMaxDate.getDate() + 1);

      // This is for all changes after init when the editor now exists
      if (this.editor) {
        if (
          this.actionItem.content === undefined ||
          (this.actionItem.content === null && this.editor)
        ) {
          this.editor.reset();
        } else {
          this.editor.setEditorContent(this.actionItem.content);
        }
      }
    }
  }

  // eslint-disable-next-line class-methods-use-this
  setLocalStatus(status: string): StatusType {
    switch (status) {
      case 'Not Started':
        return StatusType.NOT_STARTED;
      case 'On Track':
        return StatusType.ON_TRACK;
      case 'Off Track':
        return StatusType.OFF_TRACK;
      case 'At Risk':
        return StatusType.AT_RISK;
      case 'Completed':
        return StatusType.COMPLETE;
      case 'Canceled':
        return StatusType.CANCELED;
      default:
        return StatusType.NOT_STARTED;
    }
  }

  openModal() {
    this.modal.open();
  }

  closeModal() {
    this.errorMessage = '';
    this.loadActionItemData();
    this.modal.close();
  }

  deleteActionItem(delId: number) {
    this.errorMessage = '';
    this.plansService.deleteActionItem(delId).subscribe({
      next: (resp) => {
        if (resp) {
          this.alertService.showAlert('ActionItem Deleted');
          this.store.dispatch(
            new DeleteActionItem({
              id: this.actionItem.id,
              implementation_phase_id: this.actionItem.implementation_phase_id,
              implementation_deliverable_id:
                this.actionItem.implementation_deliverable_id,
            })
          );
          this.closeModal();
        }
      },
      error: (error) => {
        this.errorMessage =
          error?.error?.message ||
          'Could not delete this action item. Please try again.';
      },
    });
  }

  setLinkedItems(linkedItems: LinkedItemSearchOption[]) {
    this.linkedItems = linkedItems;
  }

  // ADD UPDATE ASSIGNEES
  addAssignee(newAssignee: UserLiteDTO | null) {
    if (
      newAssignee &&
      !this.assignees.some((assignee) => assignee.id === newAssignee.id)
    ) {
      this.assignees.push(newAssignee);
    }
  }

  deleteAssignee(assigneeId: number) {
    this.assignees = this.assignees.filter(
      (assignee) => assignee.id !== assigneeId
    );
  }

  // eslint-disable-next-line class-methods-use-this, @typescript-eslint/no-unused-vars
  onActionItemStatusChange(status: StatusType) {
    this.selectedStatus = status;
  }

  // SET DATES
  setInitialDueDate(end: string) {
    this.defaultDueDate = new Date(end).getTime() / 1000 + 60 * 60 * 24;

    if (this.dueDatePicker && this.defaultDueDate) {
      this.dueDatePicker.selectedDate = ngbDateStructFromUnixTimestamp(
        this.defaultDueDate
      );
    }
  }

  updateDueDate(times: DatepickerOutput[]) {
    const endDate = times[0].time / 1000;
    if (
      endDate &&
      endDate <= this.datePickerMaxDate.getTime() / 1000 &&
      endDate >= this.datePickerMinDate.getTime() / 1000 - 60 * 60 * 24
    ) {
      this.defaultDueDate = endDate;
    }
    if (this.dueDatePicker && this.defaultDueDate) {
      this.dueDatePicker.selectedDate = ngbDateStructFromUnixTimestamp(
        this.defaultDueDate
      );
    }
    this.f['dueDate'].setValue(
      dateFromUnixTimestamp(this.defaultDueDate * 1000)
    );

    this.cdr.detectChanges();
  }

  submitActionItemData() {
    this.formSubmitted = true;
    this.isLoading = true;
    this.errorMessage = '';

    this.editActionItemPayload = {
      title: this.f['title'].value,
      description: this.f['description'].value,
      status: this.selectedStatus,
      content: this.updatedContent || '',
      due_date: this.f['dueDate'].value,
      linked_items: this.linkedItems,
    };

    this.plansService
      .updateActionItem(this.actionItem.id, this.editActionItemPayload)
      .subscribe({
        next: (res) => {
          this.store.dispatch(new EditActionItem(res.item));
          this.modal.close();
          this.isLoading = false;
        },
        error: (error) => {
          this.errorMessage =
            error?.error?.message ||
            'Could not save this action item. Please try again.';
          this.isLoading = false;
        },
      });

    // If user was not already assigned to the action item, then assign the user
    this.assignees.forEach((assignee) => {
      if (
        !this.actionItem.assignments.some(
          (assignment) => assignment.user.id === assignee.id
        )
      ) {
        this.plansService
          .addActionItemAssignee(this.actionItem.id, assignee.id)
          .subscribe((response) => {
            const assigneePusher: PusherAssignmentChangesDTO = {
              for: 'implementation_action_item',
              for_id: this.actionItem.id,
              implementation_phase_id: this.actionItem.implementation_phase_id,
              implementation_deliverable_id:
                this.actionItem.implementation_deliverable_id,
              assignment: response.item,
            };
            this.store.dispatch(new AddAssignment(assigneePusher));
          });
      }
    });

    // If user was already assigned to the action item, but was removed during editing, delete the assignment
    this.actionItem.assignments.forEach((assignment) => {
      if (
        !this.assignees.some((assignee) => assignee.id === assignment.user.id)
      ) {
        this.plansService
          .deleteActionItemAssignee(this.actionItem.id, assignment.user.id)
          .subscribe((response) => {
            if (response) {
              const assigneePusher: PusherAssignmentChangesDTO = {
                for: 'implementation_action_item',
                for_id: this.actionItem.id,
                implementation_phase_id:
                  this.actionItem.implementation_phase_id,
                implementation_deliverable_id:
                  this.actionItem.implementation_deliverable_id,
                assignment,
              };
              this.store.dispatch(new DeleteAssignment(assigneePusher));
            }
          });
      }
    });
  }

  updateNotes(newNotes: string) {
    this.updatedContent = newNotes;
  }

  // ATTACHMENTS
  updateAttachments(file: FileDTO) {
    this.attachments.push(file);
    this.fileListComponent.fileListUpdate();
  }

  deleteAttachment(file: FileDTO) {
    this.attachments = this.attachments.filter(
      (attachment) => attachment.id !== file.id
    );
  }

  getAttachments() {
    this.plansService
      .getActionItemAttachments(this.actionItem.id)
      .subscribe((response) => {
        this.attachments = response;
      });
  }

  getComments() {
    if (this.commentRequest) this.commentRequest.unsubscribe();
    this.commentRequest = this.commentsService
      .getComments('implementation_action_item', this.actionItem.id)
      .subscribe((response) => {
        if (response) {
          this.commentList = response.items;
        }
      });

    this.subscriptions.add(this.commentRequest);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
